import {useEffect, useState, useRef, useMemo} from "react";
import { AccountData } from "./components/AccountScreen";
import {ShippingInfoData} from "./components/PatientShippingInfo";
import {QuestionnaireAnswers, QuestionnaireQuestion} from "./types/questionnaire";
import {calculateUnifiedProgress, getCurrentQuestionStep} from "./lib/progress-calculator";
import {getNamespaceTranslations} from "./lib/questionnaire-translations";
import {getQuestionDescription, getQuestionTitle} from "./lib/question-descriptions";
import {getStorefrontConfig, getStorefrontUrl} from "./lib/storefront-config";
import {setShopifyCustomerState, getShopifyCustomerState} from "./lib/shopify-customer-creation";
import {normalizeInput} from "./lib/normalize-input";
import {getMdiCustomerToken, clearMdiCustomerToken} from "../../../../scripts/lib/mdiCustomerToken";
import {FlowStepRenderer, FlowStep, FlowState, CheckoutCartData} from "./components/FlowStepRenderer";
import {
    FLOW_ORDER,
    getAdjacentStep,
    shouldSkipAccountDetails,
    isPastAccountCreationStep,
} from "./lib/flow-steps";
import {useFormAnalytics, trackFormComplete} from "./lib/form-analytics";
import {isMensProduct} from "./lib/subscription-frequency";
import {enrichKlaviyoProfile} from "./lib/klaviyo-subscribe";
import {
    type GenderAudience,
    customerTagsToGenderRole,
    resolveProductGenderAudience,
    canCustomerPurchaseProduct,
    resolveCustomerTagsForGenderRole,
    extractGenderMetafieldFromStorefrontProduct,
    alertGenderPurchaseMismatch,
} from "./lib/gender-purchase-eligibility";

const GENDER_GATED_FLOW_STEPS: FlowStep[] = [
    "questionnaire",
    "dose-selection",
    "quantity-selection",
    "subscription-selection",
    "account-details",
    "shipping-info",
    "file-upload",
    "cart-summary",
    "checkout",
];

// Extend Window interface to include Shopify and dataLayer
declare global {
    interface Window {
        Shopify: {
            routes: {
                root: string;
            };
        };
        dataLayer: Array<Record<string, unknown>>;
    }
}

interface MDFormProps {
    dataSet: {
        productId: string;
        productUrl: string;
        productHandle: string;
        productTitle?: string;
        productTemplate?: string;
        esQuestions: string;
        enQuestions: string;
        activeLocale: string;
        logoUrl?: string;
        logoUrlAlt?: string;
        welcomeHeroImage?: string;
        welcomeHeroImageAlt?: string;
        customerLoggedIn?: string;
        customerEmail?: string;
        customerId?: string;
        customerTags?: string;
        mdiData?: string;
        mdiCustomerId?: string;
        consultationProductId?: string;
        consultationProductPrice?: string;
        consultationProductHandle?: string;
        consultationProductTitle?: string;
        consultationPageTitle?: string;
        shippingFee?: string;
        shippingFeeEnabled?: string;
        fileUploadGuideTitle?: string;
        productTags?: string;
        productGender?: string;
    }
}

// FlowStep, FlowState, and CheckoutCartData types are imported from FlowStepRenderer

interface CookieProps extends FlowState {
    currentQuestionIndex: any;
    questions: any;
}

// Helper function to get locale-prefixed checkout URL
// This ensures users are redirected to the correct locale after checkout
const getCheckoutUrl = (language: 'en' | 'es'): string => {
    const localePrefix = language === 'en' ? '/en' : '';
    return `${localePrefix}/checkout`;
};

const getDefaultCartAddErrorMessage = (language: 'en' | 'es'): string =>
    language === 'es'
        ? 'No se pudo agregar el producto al carrito. Puede estar agotado o no disponible. Por favor, intenta de nuevo o contacta a soporte.'
        : 'Unable to add the product to your cart. It may be out of stock or unavailable. Please try again or contact support.';


const hasAnswers = (value: unknown): value is QuestionnaireAnswers =>
    !!value && typeof value === 'object' && Object.keys(value as object).length > 0;

const parseCartAddError = (data: unknown, language: 'en' | 'es'): string => {
    const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const description = typeof body.description === 'string' ? body.description.trim() : '';
    const message = typeof body.message === 'string' ? body.message.trim() : '';

    // Shopify often uses a generic "Cart Error" message with the useful text in description
    if (description) return description;
    if (message && message.toLowerCase() !== 'cart error') return message;
    return getDefaultCartAddErrorMessage(language);
};

/** Clear cart then add items via Ajax API. Throws a user-facing Error on failure. */
const clearAndAddToCart = async (
    items: CheckoutCartData['items'],
    language: 'en' | 'es'
): Promise<void> => {
    await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
    });

    let data: unknown = {};
    try {
        data = await addResponse.json();
    } catch (err) {
        console.error('[MDiForm] Failed to parse /cart/add.js response as JSON', {
            status: addResponse.status,
            error: err,
        });
    }

    const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const statusCode = typeof body.status === 'number' ? body.status : undefined;

    if (!addResponse.ok || (statusCode !== undefined && statusCode >= 400)) {
        throw new Error(parseCartAddError(data, language));
    }
};

const MDiForm = (props: MDFormProps): React.ReactElement | null => {
    const {
        dataSet: {
            productId,
            productUrl,
            productHandle,
            productTitle,
            productTemplate,
            esQuestions,
            enQuestions,
            activeLocale,
            logoUrl,
            logoUrlAlt,
            welcomeHeroImage,
            welcomeHeroImageAlt,
            customerLoggedIn,
            customerEmail,
            customerId,
            customerTags,
            mdiData,
            mdiCustomerId,
            consultationProductId,
            consultationProductPrice,
            consultationProductHandle,
            consultationProductTitle,
            consultationPageTitle,
            shippingFee,
            shippingFeeEnabled,
            fileUploadGuideTitle,
            productGender,
        }
    } = props;

    const language: "en" | "es" = activeLocale === "es" ? "es" : "en";
    const [questions, setQuestions] = useState<QuestionnaireQuestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [product, setProduct] = useState<any>({});
    const productGenderAudience = useMemo((): GenderAudience => {
        return resolveProductGenderAudience({
            pageGenderMetafield: productGender,
            graphGenderMetafield: extractGenderMetafieldFromStorefrontProduct(product),
        });
    }, [productGender, product]);
    const [questionnaireId, setQuestionnaireId] = useState<string>("");
    const [consultationVariantId, setConsultationVariantId] = useState<string | null>(null);
    // Kept outside flowState (and its cookie persistence) since a raw File isn't JSON-serializable —
    // this only needs to survive in-memory SPA navigation away from and back to file-upload.
    const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const isRedirectingToCheckout = useRef(false); // Prevents multiple cart rebuild operations
    const hasNavigatedRef = useRef(false);
    const justCheckedProgressRef = useRef(false);

  // Main flow state
  const [flowState, setFlowState] = useState<FlowState>({
    step: 'welcome',
    accountData: null,
    patientInfo: null,
    answers: {},
    doseSelection: null,
    quantitySelection: null,
    shippingInfo: null,
    subscriptionSelection: null,
    caseId: window.location.search?.replace('?','')?.split('&').filter((query) => { return query.split('=')[0]=='case_id'; })[0]?.split('=')[1] ?? '',
    accountSubStep: 'email',
    currentQuestionId: '',
  });

  // API-based progress state
  const [cookie, setCookie] = useState<CookieProps | null | false>(false); // false = loading, null = no progress, object = has progress
  const [isCookieLoading, setIsCookieLoading] = useState(false);
  const [getCustomerEmail, setCustomerEmail] = useState<string>(customerEmail || '');
  const [customerAccessToken, setCustomerAccessToken] = useState<string | null>(() => {
    // A cached token is only meaningful for the customer with an active Shopify session on
    // this page load. A guest (e.g. just logged out and starting a new account) has no
    // session to vouch for it, so a leftover cookie from a previous customer is stale and
    // must not be trusted here - using it would pair a new email with someone else's token.
    if (customerLoggedIn !== 'true') {
      clearMdiCustomerToken();
      return null;
    }
    return getMdiCustomerToken();
  });
  const [hasFetchedProgress, setHasFetchedProgress] = useState(false); // Track if we've already fetched progress this session
  const [skipProgressFetch, setSkipProgressFetch] = useState(false); // Flag to skip progress fetch for fresh accounts
  const [isInitializing, setIsInitializing] = useState(true); // Track if we're still initializing (fetching progress)

  // Initialize accountData for logged-in users on page load
  useEffect(() => {
    const isLoggedIn = customerLoggedIn === "true";
    const hasShopifyId = !!customerId;
    const hasMdiId = !!mdiCustomerId;
    const hasEmail = !!customerEmail;

    // If user is logged in with both accounts, pre-populate accountData
    if (isLoggedIn && hasShopifyId && hasMdiId && hasEmail && !flowState.accountData) {
        const tagsFromPage = customerTags
            ? customerTags.split(",").map((s) => s.trim()).filter(Boolean)
            : [];
        setFlowState(prev => ({
            ...prev,
            accountData: {
              customer_id: mdiCustomerId,
              shopify_customer_id: customerId,
              email: customerEmail,
              has_shopify_account: true,
              has_mdi_account: true,
              customerAccessToken: customerAccessToken, // Token from cookie if available
              shopifyCustomerTags: tagsFromPage.length ? tagsFromPage : undefined,
            }
        }));
    }
}, [customerLoggedIn, customerId, mdiCustomerId, customerEmail, customerAccessToken, customerTags, flowState.accountData]); // Only run on mount or when these values 


  // Async function to get progress from API
  const getCookie = async (): Promise<void> => {
      if (!getCustomerEmail || isCookieLoading) {
          return;
      }

      if (!customerAccessToken) {
          return;
      }

      // This always has to fetch — we can't know whether saved progress is past account
      // creation (shipping-info onward) without asking. The restore effect below is what
      // actually restricts what we act on to that tail (see isPastAccountCreationStep).
      setIsCookieLoading(true);
      setHasFetchedProgress(true); // Mark that we've fetched progress

      const payload: any = {
          product_url: window.location.href.split('?')[0],
          product_name: productTitle,
          email: getCustomerEmail,
          customer_access_token: customerAccessToken || null,
      };

      if (flowState.caseId) {
          payload.case_id = flowState.caseId;
      }

      try {
          const response = await fetch('/apps/rx/progress/get', {
              method: 'POST',
              headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
          });

          if (!response.ok) {
              // If we get a 500 or auth error, the token might be invalid - clear it
              if (response.status === 500 || response.status === 401 || response.status === 422) {
                  setCustomerAccessToken(null);
                  // Clear the cookie as well
                  document.cookie = 'mdi_customer_token=; path=/; max-age=0';
                  // Clear token from flowState.accountData
                  setFlowState(prev => {
                      if (prev.accountData) {
                          return {
                              ...prev,
                              accountData: {
                                  ...prev.accountData,
                                  customerAccessToken: null
                              }
                          };
                      }
                      return prev;
                  });
              }

              setCookie(prev => (prev && typeof prev === 'object') ? prev : null);
              return;
          }

          const data = await response.json();

          if (data && data.progress) {
              setCookie(data.progress as CookieProps);
          } else {
              setCookie(prev => (prev && typeof prev === 'object') ? prev : null);
          }
      } catch {
          setCookie(prev => (prev && typeof prev === 'object') ? prev : null);
      } finally {
          setIsCookieLoading(false);
      }
  };

  // Async function to update progress via API
  const updateCookie = async (progress: string, data?: object, overrideCaseId?: string): Promise<void> => {
      // Prefer values from this call's accountData — setState won't have flushed yet
      const incomingAccountData =
          data && Object.prototype.hasOwnProperty.call(data, 'accountData')
              ? (data as { accountData?: AccountData | null }).accountData
              : undefined;

      // If accountData is being set, update the customer email and token
      if (incomingAccountData) {
          if (!incomingAccountData.has_shopify_account && !incomingAccountData.has_mdi_account) {
              setSkipProgressFetch(true);
          } else if (incomingAccountData.has_shopify_account || incomingAccountData.has_mdi_account) {
              setSkipProgressFetch(false);
          }

          setCustomerEmail(incomingAccountData.email);
          if (incomingAccountData.customerAccessToken) {
              if (incomingAccountData.progressJustChecked) {
                  justCheckedProgressRef.current = true;
                  setHasFetchedProgress(true);
              } else if (incomingAccountData.customerAccessToken !== customerAccessToken && !justCheckedProgressRef.current) {
                  setHasFetchedProgress(false);
              }
              setCustomerAccessToken(incomingAccountData.customerAccessToken);
              // Also store in cookie for persistence
              document.cookie = `mdi_customer_token=${incomingAccountData.customerAccessToken}; path=/; max-age=86400; SameSite=Strict; Secure`;
          }
      }

      // Update cookie locally
      const cookieVals: CookieProps = Object.assign(
          cookie && typeof cookie === 'object' ? cookie : { step: progress } as CookieProps,
          data
      );

      const explicitAnswers =
          data && Object.prototype.hasOwnProperty.call(data, 'answers')
              ? (data as { answers?: QuestionnaireAnswers }).answers
              : undefined;
      cookieVals.answers = hasAnswers(explicitAnswers)
          ? explicitAnswers
          : hasAnswers(cookieVals.answers)
              ? cookieVals.answers
              : flowState.answers ?? {};

      // Update step and set cookie if step changed or if there's new data
      if (cookieVals.step !== progress || data) {
          cookieVals.step = progress as FlowStep;
          setCookie(cookieVals);
      }

      const email =
          incomingAccountData?.email ||
          getCustomerEmail ||
          flowState.accountData?.email ||
          '';
      const token =
          incomingAccountData?.customerAccessToken ||
          customerAccessToken ||
          flowState.accountData?.customerAccessToken ||
          null;
      // case_id is optional — backend identifies progress via email + product_url + token
      const effectiveCaseId = overrideCaseId || flowState.caseId || '';

      // Update progress remotely when we can identify the customer (no case_id required).
      // Only synced from shipping-info onward (see isPastAccountCreationStep) — a customer that
      // far in is already logged in on refresh and has nothing to fall back to otherwise; general
      // sync for the earlier part of the flow isn't worth persisting. Local `setCookie` above
      // still runs regardless, for in-session state.
      if (email && token && !isCookieLoading && isPastAccountCreationStep(progress as FlowStep)) {
          if (!hasAnswers(cookieVals.answers)) {
              console.error('[MDiForm] Syncing progress with empty answers at step', progress, cookieVals);
          }
          setIsCookieLoading(true);
          const payload = JSON.stringify({
              case_id: effectiveCaseId,
              step: progress,
              data: cookieVals,
              email,
              product_url: window.location.href.split('?')[0],
              customer_access_token: token,
          });
          const postOnce = async (): Promise<boolean> => {
              try {
                  const res = await fetch('/apps/rx/progress/update', {
                      method: 'POST',
                      headers: {
                          Accept: 'application/json',
                          'Content-Type': 'application/json',
                      },
                      body: payload,
                  });
                  return res.ok;
              } catch {
                  return false;
              }
          };
          try {
              // Occasionally 404s specifically on the first save right after account-creation —
              // most likely the backend momentarily not yet caught up with a brand-new Shopify
              // customer/token.
              const ok = await postOnce();
              if (!ok) {
                  await new Promise((resolve) => setTimeout(resolve, 800));
                  await postOnce();
              }
          } finally {
              setIsCookieLoading(false);
          }
      }
  };

  // Sync customer email and token from props or account data
  useEffect(() => {
      if (flowState.accountData?.email) {
          setCustomerEmail(flowState.accountData.email);
          if (flowState.accountData.customerAccessToken) {
              setCustomerAccessToken(flowState.accountData.customerAccessToken);
          }
      } else if (customerEmail) {
          setCustomerEmail(customerEmail);
      }
  }, [customerEmail, flowState.accountData?.email, flowState.accountData?.customerAccessToken]);

  useEffect(() => {
      if (isInitializing || flowState.selectedState || !customerAccessToken) return;

      let cancelled = false;
      const currentHost = window.location.host;
      const { storeFrontAPI } = getStorefrontConfig(currentHost);
      const storeFrontUrl = getStorefrontUrl(currentHost);

      getShopifyCustomerState({ customerAccessToken, storeFrontUrl, storeFrontAPI }).then((state) => {
          if (cancelled || !state) return;
          setFlowState((prev) => (prev.selectedState ? prev : { ...prev, selectedState: state }));
      });

      return () => {
          cancelled = true;
      };
  }, [isInitializing, flowState.selectedState, customerAccessToken]);

  // Scroll the modal back to the top on every screen/question change
  useEffect(() => {
      modalRef.current?.scrollTo({ top: 0 });
  }, [flowState.step, flowState.currentQuestionId]);

  // Fetch progress on mount (when identifier or product changes)
  // Fetch when we have email and a real token — the backend can only match progress on the
  // token itself, so being logged in on-site without one isn't enough — UNLESS the account was
  // just created.
  useEffect(() => {
      const hasAuthToken = !!customerAccessToken;

      // If no email is available, we can't identify the user - start fresh
      if (!getCustomerEmail) {
          setIsInitializing(false);
          return;
      }

      // No token yet - can't fetch progress, start fresh (may still be routed to the login
      // screen separately if logged in on-site, see handleWelcomeContinue)
      if (!hasAuthToken) {
          setIsInitializing(false);
          return;
      }

      // Check the skipProgressFetch flag set when fresh account is created
      if (skipProgressFetch) {
          setIsInitializing(false);
          return;
      }

      // Only fetch if we haven't fetched yet
      if (!hasFetchedProgress) {
          getCookie();
      }
  }, [getCustomerEmail, customerAccessToken, customerLoggedIn, customerId, mdiCustomerId, productId, hasFetchedProgress, skipProgressFetch]);

  // Restore progress when cookie is loaded
  useEffect(() => {
      if (hasNavigatedRef.current) {
          return;
      }

      // Cookie is still loading
      if (cookie === false) {
          return;
      }

      // No saved progress exists, start fresh
      if (cookie === null) {
          updateCookie(flowState.step);
          setIsInitializing(false);
          return;
      }

      if (!FLOW_ORDER.includes(cookie.step)) {
          updateCookie(flowState.step);
          setIsInitializing(false);
          return;
      }

      // Security check: Only restore progress if authenticated. A `cookie` value only ever gets
      // here via a successful fetch, which itself already required a real token (see getCookie),
      // so this is a defensive re-check rather than the primary gate.
      const isLoggedInOnSite = customerLoggedIn === 'true' && !!customerId;
      const hasAuthToken = !!customerAccessToken;

      if (!hasAuthToken) {
          setCookie(null);
          setIsInitializing(false);
          return;
      }

      // Only resume from shipping-info onward (see isPastAccountCreationStep). Saved progress
      // earlier than that is treated as if there were nothing to restore; the checkout-specific
      // branches below still run for anyone in that tail.
      if (!isPastAccountCreationStep(cookie.step)) {
          setCookie(null);
          setIsInitializing(false);
          return;
      }

      // Determine the correct step to restore
      const accountData = cookie.accountData || flowState.accountData || null;
      const shouldSkipAccountScreen = cookie.step === 'email-capture' &&
                                       accountData &&
                                       accountData.customer_id &&
                                       (accountData.customerAccessToken || isLoggedInOnSite);

      const restoredStep = shouldSkipAccountScreen ? 'questionnaire' : cookie.step;

      const tagListForRestore = resolveCustomerTagsForGenderRole(
          { accountData: accountData ?? undefined },
          flowState.accountData,
          customerTags
      );
      const restoreTouchesFunnel =
          GENDER_GATED_FLOW_STEPS.includes(restoredStep) ||
          (cookie.step === "checkout" && !!(cookie.checkoutCartData || cookie.checkoutUrl));
      if (
          restoreTouchesFunnel &&
          !canCustomerPurchaseProduct(customerTagsToGenderRole(tagListForRestore), productGenderAudience)
      ) {
          alertGenderPurchaseMismatch(language);
          setCookie(null);
          setFlowState((prev) => ({
              ...prev,
              step: "welcome",
              answers: {},
              doseSelection: null,
              quantitySelection: null,
              subscriptionSelection: null,
              shippingInfo: null,
              caseId: "",
              currentQuestionId: "",
              checkoutCartData: undefined,
              checkoutUrl: "",
          }));
          setIsInitializing(false);
          return;
      }

      // Handle checkout step - use Cart API to rebuild cart with selling plan
      const modalElement = document.querySelector('[data-app-layer-portal="form-portal"]');
      const isModalVisible = modalElement && !modalElement.classList.contains('hidden');

      if (cookie.step === 'checkout' && cookie.checkoutCartData) {
          // Only redirect to checkout if the modal is open
          if (!isModalVisible) {
              setFlowState(prev => ({
                  ...prev,
                  step: 'checkout',
                  checkoutCartData: cookie.checkoutCartData,
              }));
              setIsInitializing(false);
              return;
          }

          // Prevent multiple cart rebuild operations
          if (isRedirectingToCheckout.current) {
              setIsInitializing(false);
              return;
          }
          isRedirectingToCheckout.current = true;

          const { items, checkoutParams } = cookie.checkoutCartData;

          let checkoutUrl = getCheckoutUrl(language);
          const checkoutParamsString = new URLSearchParams(checkoutParams).toString();
          if (checkoutParamsString) {
              checkoutUrl += '?' + checkoutParamsString;
          }

          const rebuildCartAndCheckout = async () => {
              try {
                  await clearAndAddToCart(items, language);
                  window.location.href = checkoutUrl;
              } catch (err) {
                  isRedirectingToCheckout.current = false;
                  setIsInitializing(false);
                  setFlowState(prev => ({
                      ...prev,
                      step: 'file-upload',
                      checkoutCartData: cookie.checkoutCartData,
                  }));
                  const message =
                      err instanceof Error && err.message
                          ? err.message
                          : getDefaultCartAddErrorMessage(language);
                  alert(message);
              }
          };

          rebuildCartAndCheckout();
          return;
      }

      // Backward compatibility: Handle old checkoutUrl format
      if (cookie.step === 'checkout' && cookie.checkoutUrl && !cookie.checkoutCartData) {
          if (!isModalVisible) {
              setFlowState(prev => ({
                  ...prev,
                  step: 'checkout',
                  checkoutUrl: cookie.checkoutUrl,
              }));
              setIsInitializing(false);
              return;
          }
          window.location.href = cookie.checkoutUrl;
          return;
      }

      setFlowState(prev => {
          let mergedAccountData: AccountData | null = null;
          if (prev.accountData || cookie.accountData) {
              mergedAccountData = {
                  ...prev.accountData,
                  ...cookie.accountData,
                  customerAccessToken: prev.accountData?.customerAccessToken || cookie.accountData?.customerAccessToken || null,
                  email: cookie.accountData?.email || prev.accountData?.email || '',
              } as AccountData;
          }

          return {
              ...prev,
              step: restoredStep,
              accountData: mergedAccountData,
              patientInfo: cookie.patientInfo || prev.patientInfo || null,
              answers: hasAnswers(cookie.answers) ? cookie.answers : prev.answers,
              doseSelection: cookie.doseSelection || prev.doseSelection || null,
              quantitySelection: cookie.quantitySelection || prev.quantitySelection || null,
              subscriptionSelection: cookie.subscriptionSelection || prev.subscriptionSelection || null,
              shippingInfo: cookie.shippingInfo || prev.shippingInfo || null,
              caseId: cookie.caseId || prev.caseId || '',
              accountSubStep: cookie.accountSubStep || prev.accountSubStep || 'email',
              currentQuestionId: cookie.currentQuestionId || '',
              checkoutUrl: cookie.checkoutUrl || '',
              questionnaireReturnStep:
                  (cookie as CookieProps).questionnaireReturnStep ?? prev.questionnaireReturnStep,
              accountDetailsStep:
                  (cookie as CookieProps).accountDetailsStep ?? prev.accountDetailsStep,
              accountReturnSubStep:
                  (cookie as CookieProps).accountReturnSubStep ?? prev.accountReturnSubStep,
              accountReturnAccountDetailsStep:
                  (cookie as CookieProps).accountReturnAccountDetailsStep ??
                  prev.accountReturnAccountDetailsStep,
              emailCaptureReturnStep:
                  (cookie as CookieProps).emailCaptureReturnStep ?? prev.emailCaptureReturnStep,
              emailCaptureResumeQuestionId:
                  (cookie as CookieProps).emailCaptureResumeQuestionId ?? prev.emailCaptureResumeQuestionId,
              selectedState: cookie.selectedState || cookie.accountData?.state || prev.selectedState || '',
          };
      });

      setIsInitializing(false);
  }, [cookie, customerAccessToken, productGenderAudience, customerTags, language]);

  const accountTagSignature = resolveCustomerTagsForGenderRole(
      undefined,
      flowState.accountData,
      customerTags
  ).join("|");

  useEffect(() => {
      if (isInitializing) return;
      if (!GENDER_GATED_FLOW_STEPS.includes(flowState.step)) return;
      const tagList = resolveCustomerTagsForGenderRole(undefined, flowState.accountData, customerTags);
      if (canCustomerPurchaseProduct(customerTagsToGenderRole(tagList), productGenderAudience)) return;

      alertGenderPurchaseMismatch(language);
      setCookie(null);
      setFlowState((prev) => ({
          ...prev,
          step: "welcome",
          answers: {},
          doseSelection: null,
          quantitySelection: null,
          subscriptionSelection: null,
          shippingInfo: null,
          caseId: "",
          currentQuestionId: "",
          checkoutCartData: undefined,
          checkoutUrl: "",
      }));
  }, [productGenderAudience, flowState.step, accountTagSignature, language, isInitializing]);

  // Calculate unified progress across entire journey
  const currentQuestionIndex = flowState.currentQuestionId
    ? getCurrentQuestionStep(questions, flowState.answers, flowState.currentQuestionId)
    : 0;

  const progress = calculateUnifiedProgress(
    flowState.step,
    flowState.accountSubStep || null,
    flowState.accountDetailsStep || null,
    questions,
    flowState.answers,
    currentQuestionIndex,
    !shouldSkipAccountDetails(flowState),
    flowState.emailCaptureReturnStep === 'questionnaire'
  );

  const { currentStep, totalSteps, sectionIndex, totalSections, sectionId } = progress;

  // Get current question title for analytics tracking
  const currentQuestionTitle = flowState.currentQuestionId
    ? questions.find(q => q.partner_questionnaire_question_id === flowState.currentQuestionId)?.title || ''
    : '';

  // Get the questionnaire ID based on locale for form tracking
  const currentFormId = activeLocale === 'es' && esQuestions ? esQuestions : enQuestions;

  // Track form analytics - fires dataLayer events on step changes
  useFormAnalytics({
    step: flowState.step,
    productHandle: productHandle,
    formId: currentFormId,
    accountSubStep: flowState.accountSubStep,
    accountDetailsStep: flowState.accountDetailsStep,
    currentQuestionId: flowState.currentQuestionId,
    currentQuestionTitle: currentQuestionTitle,
  });

    // Check if customer is logged in and has MDI account
    useEffect(() => {
        const isLoggedIn = customerLoggedIn === 'true';

        if (isLoggedIn && customerEmail && customerId) {
            // Call API to check customer status and get Shopify access token
            const checkCustomerStatus = async () => {
                try {
                    const response = await fetch(`/apps/rx/customer`, {
                        method: "POST",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ email: customerEmail }),
                    });

                    // Customer status fetched - user will authenticate via AccountLogin/AccountRegister
                    await response.json();
                } catch {
                    // Silently fail - customer status check is non-critical
                }
            };

            checkCustomerStatus();
        }
    }, [customerLoggedIn, customerEmail, customerId]);

    // Fetch questionnaire questions
    useEffect(() => {
        const fetchQuestionnaire = async () => {
            setIsLoading(true);

            let currentQuestionnaireId = activeLocale === 'es' && esQuestions ? esQuestions : enQuestions;
            if (!currentQuestionnaireId) {
                currentQuestionnaireId = "9bd5bdc8-1de0-41a3-82e8-0bc7adc74ac3"; // Default to EN
            }

            setQuestionnaireId(currentQuestionnaireId);

            const questionLanguage = (activeLocale === 'es' ? 'es' : 'en') as "en" | "es";
            const MESSAGE_TO_DOCTOR_IDS = [
                "8310421f-ae56-4eef-b8f5-563b5a8d841c",
                "afcd2c8b-d802-4a0a-8e4d-24c952c4510d",
                "4331230b-a18a-4164-9a2f-fcb271b3d4fd",
                "113587f0-3bdf-48a2-abb4-728322c3519c",
            ];

            const questionsData = await fetch(
                `/apps/rx/questionnaires/${currentQuestionnaireId}/questions`,
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                }
            )
                .then(res => res.json())
                .then(res => res?.questions ?? [])
                .then(questions => questions.map((question: any) => {
                    question.order = question.order + 10;
                    if (MESSAGE_TO_DOCTOR_IDS.includes(question.partner_questionnaire_question_id)) {
                        const descOverride = getQuestionDescription(question.partner_questionnaire_question_id, questionLanguage);
                        const titleOverride = getQuestionTitle(question.partner_questionnaire_question_id, questionLanguage);
                        if (descOverride) question.description = descOverride;
                        if (titleOverride) question.title = titleOverride;
                    }
                    return question;
                }));

            const customQuestionsT = getNamespaceTranslations(questionLanguage, "customQuestions");

            const preQuestions: QuestionnaireQuestion[] = [
                {
                    "partner_questionnaire_question_id": "medication-check",
                    "title": customQuestionsT?.medicalConditions?.title || "",
                    "description": null,
                    "label": customQuestionsT?.medicalConditions?.label || "",
                    "placeholder": customQuestionsT?.medicalConditions?.placeholder || "",
                    "is_important": false,
                    "is_critical": false,
                    "display_in_pdf": false,
                    "is_optional": false,
                    "is_visible": true,
                    "has_next_button": true,
                    "has_back_button": true,
                    "mapped_to_field": null,
                    "order": 1,
                    "type": "single_option_with_textarea",
                    "file_type": null,
                    "default_value": null,
                    "feed_ads": null,
                    "rule_type": "and",
                    "hold_status": false,
                    "is_additional_approval_needed": false,
                    "options": [
                        {
                            "partner_questionnaire_question_option_id": "medical-condition-no",
                            "title": customQuestionsT?.medicalConditions?.optionNo || "",
                            "value": null,
                            "option": "No",
                            "is_important": false,
                            "is_critical": false,
                            "is_show_additional_field": false,
                            "order": 1,
                            "icon_type": null,
                            "icon": null
                        },
                        {
                            "partner_questionnaire_question_option_id": "medical-condition-yes",
                            "title": customQuestionsT?.medicalConditions?.optionYes || "",
                            "value": null,
                            "option": "Yes",
                            "is_important": false,
                            "is_critical": true,
                            "is_show_additional_field": true,
                            "order": 2,
                            "icon_type": null,
                            "icon": null
                        }
                    ],
                    "attachments": [],
                    "rules": []
                }
            ];

            const finalList = [...preQuestions, ...questionsData];
            setQuestions(finalList);
            setIsLoading(false);
        };

        if (questions.length === 0) {
            fetchQuestionnaire();
        }
    }, [flowState.step, activeLocale, esQuestions, enQuestions]);

    // Fetch product data
    useEffect(() => {
        const currentHost = window.location.host;
        const { storeFrontAPI, storeFrontShop } = getStorefrontConfig(currentHost);
        const storeFrontUrl = getStorefrontUrl(currentHost);

        const sfQuery = {
            query: `query @inContext(language: ${language.toUpperCase()}) {
        product(handle:"${productHandle}") {
          id
          handle
          title
          description
          images(first:1) {
            edges {
              node {
                src
              }
            }
          }
          options {
            name
            values
          }
          variants(first:10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "variant_badge"}
                  {namespace: "custom", key: "variant_description"}
                  {namespace: "custom", key: "variant_ingredients"}
                  {namespace: "custom", key: "variant_quantity_badge"}
                  {namespace: "custom", key: "recommended_delivery_days"}
                ]) {
                  key
                  value
                  type
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          requiresSellingPlan
          sellingPlanGroups(first:10) {
            edges {
              node {
                name
                options {
                  name
                  values
                }
                sellingPlans(first: 10) {
                  edges {
                    node {
                      id
                      name
                      description
                      recurringDeliveries
                      priceAdjustments {
                        adjustmentValue {
                          ... on SellingPlanFixedAmountPriceAdjustment {
                            adjustmentAmount {
                              amount
                              currencyCode
                            }
                          }
                          ... on SellingPlanFixedPriceAdjustment {
                            price {
                              amount
                              currencyCode
                            }
                          }
                          ... on SellingPlanPercentagePriceAdjustment {
                            adjustmentPercentage
                          }
                        }
                      }
                      options {
                        name
                        value
                      }
                      billingPolicy {
                        ... on SellingPlanRecurringBillingPolicy {
                          interval
                          intervalCount
                        }
                      }
                      deliveryPolicy {
                        ... on SellingPlanRecurringDeliveryPolicy {
                          interval
                          intervalCount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          metafields(identifiers: [
            {namespace: "custom", key: "ingredients"}
            {namespace: "custom", key: "frequency_guide_page"}
            {namespace: "custom", key: "subscription_frequency_tiers"}
            {namespace: "custom", key: "gender"}
          ]) {
            key
            value
            type
            reference {
              ... on Page {
                id
                title
                body
              }
              ... on Metaobject {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      }`
        };

        if (storeFrontAPI && storeFrontShop) {
            fetch(storeFrontUrl, {
                headers: {
                    "X-Shopify-Storefront-Access-Token": storeFrontAPI,
                    "content-type": "application/json"
                },
                body: JSON.stringify(sfQuery),
                method: "POST"
            })
                .then(d => d.json())
                .then(response => {
                    if (response?.data?.product) {
                        setProduct(response.data.product);
                    }
                })
                .catch(() => {});
        }
    }, [productUrl, productHandle, language]);

    // Fetch consultation product variant ID
    useEffect(() => {
        if (!consultationProductId) return;

        const currentHost = window.location.host;
        const { storeFrontAPI } = getStorefrontConfig(currentHost);
        const storeFrontUrl = getStorefrontUrl(currentHost);

        const consultationProductGid = `gid://shopify/Product/${consultationProductId}`;

        const query = `
            query {
                product(id: "${consultationProductGid}") {
                    id
                    title
                    variants(first: 1) {
                        edges {
                            node {
                                id
                            }
                        }
                    }
                }
            }
        `;

        fetch(storeFrontUrl, {
            headers: {
                "X-Shopify-Storefront-Access-Token": storeFrontAPI,
                "content-type": "application/json"
            },
            body: JSON.stringify({ query }),
            method: "POST"
        })
            .then(res => res.json())
            .then(data => {
                const variantId = data?.data?.product?.variants?.edges?.[0]?.node?.id;
                if (variantId) {
                    setConsultationVariantId(variantId);
                }
            })
            .catch(() => {});
    }, [consultationProductId]);

    // Navigation handlers
    const handleNext = async (step: FlowStep, data?: Partial<FlowState>) => {
        hasNavigatedRef.current = true;
        justCheckedProgressRef.current = false;
        if (GENDER_GATED_FLOW_STEPS.includes(step)) {
            const tagList = resolveCustomerTagsForGenderRole(data, flowState.accountData, customerTags);
            const role = customerTagsToGenderRole(tagList);
            if (!canCustomerPurchaseProduct(role, productGenderAudience)) {
                alertGenderPurchaseMismatch(language);
                return;
            }
        }

        // If an existing user just logged in, check for saved progress before navigating. We
        // still need to fetch to find out whether they were at checkout — that recovery stays on
        // regardless of isPastAccountCreationStep below, since it's real revenue recovery rather
        // than general mid-flow resume.
        if (
            (step === 'questionnaire' || step === 'health-questions-intro') &&
            data?.accountData?.has_shopify_account &&
            data?.accountData?.customerAccessToken
        ) {
            const accountData = data.accountData;

            // Fetch progress for this user
            try {
                const progressPayload = {
                    product_url: window.location.href.split('?')[0],
                    product_name: productTitle,
                    email: accountData.email,
                    customer_access_token: accountData.customerAccessToken || null,
                };

                const progressResponse = await fetch('/apps/rx/progress/get', {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(progressPayload),
                });

                justCheckedProgressRef.current = true;
                setHasFetchedProgress(true);

                if (progressResponse.ok && !accountData.justRegistered) {
                    const progressData = await progressResponse.json();

                    if (progressData?.progress) {
                        const savedProgress = progressData.progress;

                        // If user was at checkout, restore that state and redirect
                        if (savedProgress.step === 'checkout' && savedProgress.checkoutCartData) {
                            // Prevent multiple cart rebuild operations
                            if (isRedirectingToCheckout.current) return;
                            isRedirectingToCheckout.current = true;

                            const { items, checkoutParams } = savedProgress.checkoutCartData;

                            // Update state with account data and checkout info
                            setFlowState(prev => ({
                                ...prev,
                                ...data,
                                step: 'checkout',
                                checkoutCartData: savedProgress.checkoutCartData,
                                answers: hasAnswers(savedProgress.answers) ? savedProgress.answers : prev.answers,
                                doseSelection: savedProgress.doseSelection || null,
                                quantitySelection: savedProgress.quantitySelection || null,
                                subscriptionSelection: savedProgress.subscriptionSelection || null,
                                shippingInfo: savedProgress.shippingInfo || null,
                                caseId: savedProgress.caseId || '',
                                selectedState: savedProgress.selectedState || savedProgress.accountData?.state || '',
                            }));

                            // Rebuild cart and redirect to checkout
                            let checkoutUrl = getCheckoutUrl(language);
                            const checkoutParamsString = new URLSearchParams(checkoutParams).toString();
                            if (checkoutParamsString) {
                                checkoutUrl += '?' + checkoutParamsString;
                            }

                            try {
                                await clearAndAddToCart(items, language);
                                window.location.href = checkoutUrl;
                            } catch (err) {
                                isRedirectingToCheckout.current = false;
                                setFlowState(prev => ({
                                    ...prev,
                                    ...data,
                                    step: 'file-upload',
                                    checkoutCartData: savedProgress.checkoutCartData,
                                    answers: hasAnswers(savedProgress.answers) ? savedProgress.answers : prev.answers,
                                    doseSelection: savedProgress.doseSelection || null,
                                    quantitySelection: savedProgress.quantitySelection || null,
                                    subscriptionSelection: savedProgress.subscriptionSelection || null,
                                    shippingInfo: savedProgress.shippingInfo || null,
                                    caseId: savedProgress.caseId || '',
                                    selectedState: savedProgress.selectedState || savedProgress.accountData?.state || '',
                                }));
                                const message =
                                    err instanceof Error && err.message
                                        ? err.message
                                        : getDefaultCartAddErrorMessage(language);
                                alert(message);
                            }
                            return;
                        }

                        // Resume to any other step stays limited to shipping-info onward — see
                        // isPastAccountCreationStep.
                        if (
                            isPastAccountCreationStep(savedProgress.step) &&
                            savedProgress.step &&
                            FLOW_ORDER.includes(savedProgress.step) &&
                            savedProgress.step !== 'welcome' &&
                            savedProgress.step !== 'email-capture' &&
                            savedProgress.step !== 'health-questions-intro'
                        ) {
                            updateCookie(savedProgress.step, {
                                ...data,
                                ...savedProgress,
                                answers: hasAnswers(savedProgress.answers) ? savedProgress.answers : flowState.answers,
                            });
                            setFlowState(prev => ({
                                ...prev,
                                ...data,
                                step: savedProgress.step,
                                questionnaireReturnStep: prev.step,
                                answers: hasAnswers(savedProgress.answers) ? savedProgress.answers : prev.answers,
                                doseSelection: savedProgress.doseSelection || null,
                                quantitySelection: savedProgress.quantitySelection || null,
                                subscriptionSelection: savedProgress.subscriptionSelection || null,
                                shippingInfo: savedProgress.shippingInfo || null,
                                caseId: savedProgress.caseId || '',
                                currentQuestionId: savedProgress.currentQuestionId || '',
                                selectedState: savedProgress.selectedState || savedProgress.accountData?.state || '',
                                emailCaptureReturnStep: savedProgress.emailCaptureReturnStep ?? prev.emailCaptureReturnStep,
                                emailCaptureResumeQuestionId: savedProgress.emailCaptureResumeQuestionId ?? prev.emailCaptureResumeQuestionId,
                            }));
                            return;
                        }
                    }
                }
            } catch {
                // Progress fetch failed, continue to questionnaire
            }
        }

        const isResumingFromEmailCaptureInterrupt =
            (flowState.step === 'email-capture' || flowState.step === 'health-questions-intro') &&
            flowState.emailCaptureReturnStep === 'questionnaire';

        const dataWithQuestionnaireMeta =
            step === 'questionnaire' && !isResumingFromEmailCaptureInterrupt
                ? {
                      ...data,
                      questionnaireReturnStep: flowState.step,
                      accountReturnSubStep: flowState.accountSubStep,
                      accountReturnAccountDetailsStep: flowState.accountDetailsStep,
                  }
                : data;

        updateCookie(step, dataWithQuestionnaireMeta);
        setFlowState(prev => ({ ...prev, step, ...dataWithQuestionnaireMeta }));
    };

    const handleQuestionnaireExit = () => {
        const target = flowState.questionnaireReturnStep ?? 'state-check';

        const accountSubStep =
            flowState.accountReturnSubStep ??
            flowState.accountSubStep ??
            (flowState.accountData?.has_mdi_account ? 'login' : 'email');

        const accountDetailsStep =
            flowState.accountReturnAccountDetailsStep ??
            flowState.accountDetailsStep ??
            'identity';

        void handleNext(target, {
            currentQuestionId: '',
            accountSubStep,
            accountDetailsStep,
        });
    };

    const handleWelcomeContinue = () => {
        const isLoggedInOnSite = customerLoggedIn === 'true' && !!customerId;
        // Only consider token valid if it exists in state (will be null if cleared due to API error)
        const hasValidToken = !!flowState.accountData?.customerAccessToken || !!customerAccessToken;

        // User is logged in on site but doesn't have a valid token - send to password screen to get token
        // This handles the case where user logged in via password reset but token is invalid/missing
        if (isLoggedInOnSite && !hasValidToken) {
            // Set up account data with email from Shopify session, then go to login screen
            handleNext('email-capture', {
                accountData: {
                    email: customerEmail || '',
                    customer_id: mdiCustomerId,
                    shopify_customer_id: customerId,
                    has_shopify_account: true,
                    has_mdi_account: !!mdiCustomerId,
                },
                accountSubStep: 'login' // Go directly to password screen
            });
            return;
        }

        // User has valid token and MDI account - proceed to questionnaire
        if (flowState.accountData &&
            flowState.accountData.has_mdi_account &&
            hasValidToken) {
            handleNext('questionnaire');
        } else {
            handleNext('intro');
        }
    };

  const handleStateCheckComplete = (state: string) => {
    handleNext('questionnaire', { selectedState: state });
  };

  const handleAccountBack = () => {
    handleNext(flowState.emailCaptureReturnStep ?? 'state-check');
  };

    const handleWelcomeLogin = () => {
        // TODO: Implement login flow
        handleNext('login');
    };

    const handleIntroContinue = () => {
        handleNext('state-check');
    };

    const handleQuestionnaireEmailCapture = (resumeAtQuestionId: string) => {
        handleNext('email-capture', {
            emailCaptureReturnStep: 'questionnaire',
            emailCaptureResumeQuestionId: resumeAtQuestionId,
        });
    };

    const handleHealthQuestionsIntroContinue = () => {
        handleNext('questionnaire', {
            currentQuestionId: flowState.emailCaptureResumeQuestionId ?? flowState.currentQuestionId,
        });
    };

    const handleQuestionnaireComplete = (answers: QuestionnaireAnswers) => {
        handleNext('questionnaire-summary', {answers});
    };

    const handleQuestionnaireSummaryContinue = () => {
        handleNext('dose-selection');
    };

    const handleDoseSelection = (selectedDose: string) => {
        handleNext('quantity-selection', {
            doseSelection: selectedDose,
            subscriptionSelection: (cookie && typeof cookie === 'object') ? cookie.subscriptionSelection : null // Preserve subscription selection
        });
    };

    const handleQuantitySelection = (selectedQuantity: string) => {
        const preserveSubscription =
            !isMensProduct(productHandle) &&
            cookie &&
            typeof cookie === 'object' &&
            cookie.subscriptionSelection;

        handleNext('subscription-selection', {
            quantitySelection: selectedQuantity,
            subscriptionSelection: preserveSubscription ? cookie.subscriptionSelection : null
        });
    };

    const handleFileUploadSuccess = async (response: any) => {
        // Get variants array - could be direct array or GraphQL edges format
        const variantsArray = Array.isArray(product?.variants)
            ? product.variants
            : product?.variants?.edges?.map((edge: any) => edge.node) || [];

        // Find the variant ID based on selected dose and quantity
        const selectedVariant = variantsArray.find((v: any) => {
            // Find dose option (could be "Dose", "dosis", "Dosis", etc.)
            const doseOption = v.selectedOptions?.find((opt: any) =>
                opt.name.toLowerCase().includes('dose') ||
                opt.name.toLowerCase().includes('dosis') ||
                opt.name.toLowerCase().includes('dosage')
            );

            // Find quantity option (could be "Quantity", "cantidad", etc.)
            const quantityOption = v.selectedOptions?.find((opt: any) =>
                opt.name.toLowerCase().includes('quantity') ||
                opt.name.toLowerCase().includes('cantidad') ||
                opt.name.toLowerCase().includes('tablets per month')
            );

            const variantDose = doseOption?.value;
            const variantQuantity = quantityOption?.value;

            return variantDose === flowState.doseSelection &&
                   variantQuantity === flowState.quantitySelection;
        });

        const variantId = selectedVariant?.id?.split('/').pop() || selectedVariant?.id || '';

        if (!variantId) {
            throw new Error(
                language === 'es'
                    ? 'No se encontró la variante del producto. Por favor, contacta a soporte.'
                    : 'Could not find product variant. Please contact support.'
            );
        }

        // Call finish endpoint and await its completion.
        const finishToken = customerAccessToken || flowState.accountData?.customerAccessToken || '';
        if (!finishToken) {
            throw new Error(
                language === 'es'
                    ? 'No se pudo verificar tu cuenta. Por favor, actualiza la página e inténtalo de nuevo.'
                    : 'We could not verify your account. Please refresh the page and try again.'
            );
        }

        let returnedCaseId: string | undefined;
        const finishResponse = await fetch('/apps/rx/finish', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                variant_id: variantId,
                locale: activeLocale,
                case_id: flowState.caseId || '',
                email: getCustomerEmail || flowState.accountData?.email || '',
                product_url: window.location.href.split('?')[0],
                customer_access_token: finishToken,
                questionnaire_id: questionnaireId || (activeLocale === 'es' && esQuestions ? esQuestions : enQuestions) || '',
            })
        });

        if (!finishResponse.ok) {
            throw new Error(
                language === 'es'
                    ? 'No se pudo enviar tu información. Por favor, inténtalo de nuevo.'
                    : 'We could not submit your information. Please try again.'
            );
        }

        const finishData = await finishResponse.json().catch(() => ({}));
        returnedCaseId = finishData?.case_id;

        // Prefer the case ID returned by the finish endpoint; fall back to the existing one if it's missing
        const caseId = returnedCaseId || flowState.caseId || '';

        // Get selling plan GID if subscription was selected
        const sellingPlanGid = flowState.subscriptionSelection?.sellingPlanId;

        // Determine if state surcharge applies based on shipping state
        // States that require surcharge (full names)
        const SURCHARGE_STATES = ['Oregon', 'Hawaii', 'Rhode Island', 'Maine', 'Alaska', 'Delaware', 'Kansas', 'Mississippi', 'West Virginia', 'Missouri', 'Arkansas'];

        const shippingState = flowState.shippingInfo?.state || '';
        const stateSurcharge = SURCHARGE_STATES.includes(shippingState) ? 'true' : 'false';

        // Extract numeric variant ID from GID (gid://shopify/ProductVariant/123 -> 123)
        const numericVariantId = selectedVariant.id.split('/').pop();
        const numericSellingPlanId = sellingPlanGid ? sellingPlanGid.split('/').pop() : null;
        const numericConsultationVariantId = consultationVariantId ? consultationVariantId.split('/').pop() : null;

        // Build line item properties for cart
        const lineItemProperties: Record<string, string> = {
            '_case_id': caseId,
            '_state_surcharge': stateSurcharge,
            '_accept_terms': new Date().toISOString().split('T')[0]
        };

        // Build cart items for Cart API
        // Cart API format: { items: [{ id, quantity, selling_plan?, properties? }] }
        // This supports selling plans, unlike cart permalinks!
        const cartItems: CheckoutCartData['items'] = [];

        // Main product with selling plan (if subscription selected)
        const mainItem: CheckoutCartData['items'][0] = {
            id: numericVariantId!,
            quantity: 1,
            properties: lineItemProperties
        };
        if (numericSellingPlanId) {
            mainItem.selling_plan = numericSellingPlanId;
        }
        cartItems.push(mainItem);

        // Add consultation product as second item (no selling plan)
        if (numericConsultationVariantId) {
            cartItems.push({
                id: numericConsultationVariantId,
                quantity: 1
            });
        }

        // Build checkout pre-fill parameters
        const checkoutParams: Record<string, string> = {};

        // Add customer/shipping info for checkout pre-fill
        if (flowState.accountData) {
            const normalizedFirstName = flowState.accountData.firstName
                ? normalizeInput(flowState.accountData.firstName)
                : '';
            const normalizedLastName = flowState.accountData.lastName
                ? normalizeInput(flowState.accountData.lastName)
                : '';

            if (flowState.accountData.email) {
                checkoutParams['checkout[email]'] = flowState.accountData.email;
            }
            if (normalizedFirstName) {
                checkoutParams['checkout[shipping_address][first_name]'] = normalizedFirstName;
            }
            if (normalizedLastName) {
                checkoutParams['checkout[shipping_address][last_name]'] = normalizedLastName;
            }
        }

        if (flowState.shippingInfo) {
            if (flowState.shippingInfo.address) {
                checkoutParams['checkout[shipping_address][address1]'] = flowState.shippingInfo.address;
            }
            if (flowState.shippingInfo.address2) {
                checkoutParams['checkout[shipping_address][address2]'] = flowState.shippingInfo.address2;
            }
            if (flowState.shippingInfo.city?.trim()) {
                checkoutParams['checkout[shipping_address][city]'] = normalizeInput(
                    flowState.shippingInfo.city.trim()
                );
            }
            if (flowState.shippingInfo.state) {
                checkoutParams['checkout[shipping_address][province]'] = flowState.shippingInfo.state;
            }
            if (flowState.shippingInfo.zip_code) {
                checkoutParams['checkout[shipping_address][zip]'] = flowState.shippingInfo.zip_code;
            }
            if (flowState.shippingInfo.phone) {
                checkoutParams['checkout[shipping_address][phone]'] = flowState.shippingInfo.phone;
            }
        }

        // Add United States as default country
        checkoutParams['checkout[shipping_address][country]'] = 'United States';

        // Build the checkoutCartData structure for persistence
        const checkoutCartData: CheckoutCartData = {
            items: cartItems,
            checkoutParams: checkoutParams
        };

        // Persist cart payload, then add via Cart API — only redirect on success
        try {
            await updateCookie('checkout', { checkoutCartData });
            await clearAndAddToCart(cartItems, language);

            // Build checkout URL with pre-fill params
            let checkoutUrl = getCheckoutUrl(language);
            const checkoutQueryString = new URLSearchParams(checkoutParams).toString();
            if (checkoutQueryString) {
                checkoutUrl += '?' + checkoutQueryString;
            }

            // Track form completion before redirect
            trackFormComplete(productHandle, currentFormId);
            window.location.href = checkoutUrl;
        } catch (err) {
            // Do not redirect to empty checkout — keep user on upload step
            setFlowState(prev => ({
                ...prev,
                step: 'file-upload',
                checkoutCartData,
            }));
            const message =
                err instanceof Error && err.message
                    ? err.message
                    : getDefaultCartAddErrorMessage(language);
            throw new Error(message);
        }
    };

    const handleSubscriptionSelection = (selectedFrequency: string, sellingPlanId: string) => {
        handleNext(getAdjacentStep('subscription-selection', 1, flowState), {
            subscriptionSelection: {
                frequency: selectedFrequency,
                sellingPlanId: sellingPlanId
            }
        });
    };

    const handleAccountDetailsComplete = async (data: AccountData) => {
        const mergedAccountData = { ...(flowState.accountData ?? {}), ...data };
        // enrichKlaviyoProfile, not identifyOnAccountCreation: for a brand-new registrant the
        // "Account Created" event and the Klaviyo list subscribe already fired earlier in this
        // same submit (AccountRegister's access step) / at email-capture respectively — this
        // only adds name/phone/SMS-consent, never re-fires "Account Created".
        await enrichKlaviyoProfile(mergedAccountData);

        // Write the earlier-selected state onto the Shopify default address now that the
        // Shopify account definitely exists (account creation now happens on this same screen,
        // via AccountRegister's access step, rather than right after email).
        if (data.customerAccessToken && flowState.selectedState) {
            const currentHost = window.location.host;
            const { storeFrontAPI } = getStorefrontConfig(currentHost);
            const storeFrontUrl = getStorefrontUrl(currentHost);
            setShopifyCustomerState({
                customerAccessToken: data.customerAccessToken,
                storeFrontUrl,
                storeFrontAPI,
                state: flowState.selectedState,
            });
        }

        // BE requirement: /apps/rx/progress/get must fire once, right after account
        // registration completes — this is a one-off call, separate from the ongoing
        // step-by-step resume sync gated by isPastAccountCreationStep elsewhere. We don't act on
        // the response (and deliberately don't reuse getCookie()/setCookie() here, to avoid
        // triggering the restore-on-load effect while we're already navigating to shipping-info)
        // — just fire it and wait for it to complete before moving on.
        if (data.customerAccessToken) {
            try {
                await fetch('/apps/rx/progress/get', {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        product_url: window.location.href.split('?')[0],
                        product_name: productTitle,
                        email: mergedAccountData.email,
                        customer_access_token: data.customerAccessToken,
                        ...(flowState.caseId ? { case_id: flowState.caseId } : {}),
                    }),
                });
                // Asked once above — stop updateCookie from firing a second, unawaited /get for this
                // same token change (see AccountData.progressJustChecked).
                mergedAccountData.progressJustChecked = true;
            } catch (error) {
                console.error(error);
            }
        }

        handleNext(getAdjacentStep('account-details', 1, flowState), {
            accountData: mergedAccountData,
        });
    };

    const handleShippingInfoComplete = async (data: ShippingInfoData) => {
        // If we have a customer access token, add the address to their Shopify account
        if (flowState.accountData?.customerAccessToken) {
            const currentHost = window.location.host;
            const { storeFrontAPI } = getStorefrontConfig(currentHost);
            const storeFrontUrl = getStorefrontUrl(currentHost);

            const addressMutation = `
                mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
                    customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
                        customerAddress {
                            id
                        }
                        customerUserErrors {
                            field
                            message
                        }
                    }
                }
            `;

            const addressVariables = {
                customerAccessToken: flowState.accountData.customerAccessToken,
                address: {
                    address1: data.address,
                    address2: data.address2 || '',
                    city: data.city?.trim()
                        ? normalizeInput(data.city.trim())
                        : '',
                    province: data.state,
                    zip: data.zip_code,
                    country: 'US',
                    phone: flowState.accountData.phone || '',
                    firstName: flowState.accountData.firstName ? normalizeInput(flowState.accountData.firstName) : '',
                    lastName: flowState.accountData.lastName ? normalizeInput(flowState.accountData.lastName) : ''
                }
            };

            try {
                const response = await fetch(storeFrontUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': storeFrontAPI,
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ query: addressMutation, variables: addressVariables })
                });

                await response.json();
            } catch {
                // Address creation error - continue anyway
            }
        }

        handleNext('file-upload', {shippingInfo: data});
    };

    const handleCloseModal = () => {
        const app = document.querySelector('[data-app-layer-portal="form-portal"]') as HTMLElement;
        if (app) app.classList.add('hidden');

        // Restore body overflow
        document.body.style.overflow = '';
        document.body.classList.remove('overflow-hidden');

        // Remove aria-hidden from main content when modal closes
        const mainContent = document.querySelector('main');
        if (mainContent) {
            mainContent.removeAttribute('aria-hidden');
        }

        // Update PDP button text based on current progress
        const takeQuizBtn = document.querySelector('[data-take-quiz-el]') as HTMLElement;
        if (takeQuizBtn && app) {
            // Determine correct button text based on flow state
            if (flowState.step === 'checkout' && flowState.checkoutCartData) {
                // User is at checkout - show "Go to Payment"
                const goToPaymentText = language === 'es' ? 'Ir al pago' : 'Go to Payment';
                takeQuizBtn.textContent = goToPaymentText;
            } else if (flowState.step !== 'welcome') {
                // User has progress beyond welcome - show "Continue" text
                const altText = app.dataset.altText;
                if (altText) {
                    takeQuizBtn.textContent = altText;
                }
            }
            // Return focus to the button
            takeQuizBtn.focus();
        }
      };

  const handleReset = () => {
    setFlowState({
      step: 'welcome',
      accountData: null,
      patientInfo: null,
      answers: {},
      doseSelection: null,
      quantitySelection: null,
      subscriptionSelection: null,
      caseId: '',
      accountSubStep: 'email',
      currentQuestionId: '',
      questionnaireReturnStep: undefined,
      accountDetailsStep: undefined,
      accountReturnSubStep: undefined,
      accountReturnAccountDetailsStep: undefined,
      shippingInfo: null,
      checkoutUrl: '',
    });
    setPendingUploadFile(null);
  };

    // Handle ESC key to close modal, manage aria-hidden, and focus trapping
    useEffect(() => {
        // Check if modal is actually visible (not hidden)
        const app = document.querySelector('[data-app-layer-portal="form-portal"]');
        const isModalVisible = app && !app.classList.contains('hidden');

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && flowState.step === 'welcome') {
                handleCloseModal();
            }
        };

        // Focus trap - keep focus within modal
        const handleTab = (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || !modalRef.current) return;

            // Only trap focus when modal is visible
            const currentApp = document.querySelector('[data-app-layer-portal="form-portal"]');
            if (currentApp?.classList.contains('hidden')) return;

            const focusableElements = modalRef.current.querySelectorAll(
                'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0] as HTMLElement;
            const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

            if (event.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstElement) {
                    lastElement?.focus();
                    event.preventDefault();
                }
            } else {
                // Tab
                if (document.activeElement === lastElement) {
                    firstElement?.focus();
                    event.preventDefault();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.addEventListener('keydown', handleTab);

        // Only manage aria-hidden when modal is visible.
        // Portal lives inside <main>, so never aria-hide main (hides the dialog + triggers
        // "descendant retained focus" warnings). Mark the portal as the accessible dialog root.
        const mainContent = document.querySelector('main');
        if (isModalVisible && app) {
            app.setAttribute('aria-hidden', 'false');
            app.removeAttribute('inert');

            // Only set initial focus on welcome screen (first time modal opens)
            // Avoid forcing focus on subsequent steps to prevent unwanted focus rings
            if (flowState.step === 'welcome') {
                const firstFocusable = modalRef.current?.querySelector(
                    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])'
                ) as HTMLElement;
                firstFocusable?.focus();
            }
        }

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('keydown', handleTab);
            if (mainContent) {
                mainContent.removeAttribute('aria-hidden');
            }
        };
    }, [flowState.step]);

    // Watch for modal becoming visible and redirect to checkout if needed
    // This handles the case where we're at checkout step but modal was closed
    useEffect(() => {
        const modalElement = document.querySelector('[data-app-layer-portal="form-portal"]');
        if (!modalElement) return;

        // Function to handle checkout redirect when modal becomes visible
        const handleModalVisible = () => {
            const isModalVisible = !modalElement.classList.contains('hidden');

            // Only redirect if modal just became visible AND we're at checkout step with cart data
            if (isModalVisible && flowState.step === 'checkout' && flowState.checkoutCartData) {
                // Prevent multiple cart rebuild operations (uses component-level ref)
                if (isRedirectingToCheckout.current) return;
                isRedirectingToCheckout.current = true;

                const { items, checkoutParams } = flowState.checkoutCartData;

                // Build checkout URL with pre-fill params
                let checkoutUrl = getCheckoutUrl(language);
                const checkoutParamsString = new URLSearchParams(checkoutParams).toString();
                if (checkoutParamsString) {
                    checkoutUrl += '?' + checkoutParamsString;
                }

                // Rebuild cart and redirect
                const rebuildCartAndCheckout = async () => {
                    try {
                        await clearAndAddToCart(items, language);
                        window.location.href = checkoutUrl;
                    } catch (err) {
                        isRedirectingToCheckout.current = false;
                        setFlowState(prev => ({
                            ...prev,
                            step: 'file-upload',
                        }));
                        const message =
                            err instanceof Error && err.message
                                ? err.message
                                : getDefaultCartAddErrorMessage(language);
                        alert(message);
                    }
                };

                rebuildCartAndCheckout();
            }
        };

        // Use MutationObserver to watch for class changes on the modal
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    handleModalVisible();
                }
            });
        });

        observer.observe(modalElement, { attributes: true, attributeFilter: ['class'] });

        // Also check immediately in case modal is already visible
        handleModalVisible();

        return () => observer.disconnect();
    }, [flowState.step, flowState.checkoutCartData]);

    return (
        <FlowStepRenderer
            ref={modalRef}
            flowState={flowState}
            setFlowState={setFlowState}
            isInitializing={isInitializing}
            isLoading={isLoading}
            questions={questions}
            language={language}
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            logoUrl={logoUrl}
            logoUrlAlt={logoUrlAlt}
            welcomeHeroImage={welcomeHeroImage}
            welcomeHeroImageAlt={welcomeHeroImageAlt}
            productTitle={productTitle}
            productHandle={productHandle}
            productTemplate={productTemplate}
            customerTags={customerTags}
            productGenderAudience={productGenderAudience}
            product={product}
            cookie={cookie}
            customerId={customerId}
            customerEmail={getCustomerEmail}
            customerAccessToken={customerAccessToken}
            productUrl={typeof window !== 'undefined' ? window.location.href.split('?')[0] : productUrl}
            consultationProductPrice={consultationProductPrice}
            consultationProductId={consultationProductId}
            consultationProductHandle={consultationProductHandle}
            consultationProductTitle={consultationProductTitle}
            consultationPageTitle={consultationPageTitle}
            shippingFee={shippingFee}
            shippingFeeEnabled={shippingFeeEnabled}
            fileUploadGuideTitle={fileUploadGuideTitle}
            pendingUploadFile={pendingUploadFile}
            onPendingUploadFileChange={setPendingUploadFile}
            onWelcomeContinue={handleWelcomeContinue}
            onIntroContinue={handleIntroContinue}
            onStateCheckContinue={handleStateCheckComplete}
            onCloseModal={handleCloseModal}
            onAccountBack={handleAccountBack}
            onAccountDetailsComplete={handleAccountDetailsComplete}
            onHealthQuestionsIntroContinue={handleHealthQuestionsIntroContinue}
            onQuestionnaireEmailCapture={handleQuestionnaireEmailCapture}
            onExitQuestionnaire={handleQuestionnaireExit}
            onQuestionnaireComplete={handleQuestionnaireComplete}
            onQuestionnaireSummaryContinue={handleQuestionnaireSummaryContinue}
            onDoseSelection={handleDoseSelection}
            onQuantitySelection={handleQuantitySelection}
            onSubscriptionSelection={handleSubscriptionSelection}
            onShippingInfoComplete={handleShippingInfoComplete}
            onFileUploadSuccess={handleFileUploadSuccess}
            onReset={handleReset}
            onNext={handleNext}
            updateCookie={updateCookie}
        />
    );
};

export default MDiForm;